import { useState, useRef, useEffect, useMemo } from "react";
import { Link, useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "convex/react";
import { useQuery as useApiQuery } from "@tanstack/react-query";
import { api } from "../../../convex/_generated/api";
import type { Doc, Id } from "../../../convex/_generated/dataModel";
import { useAuth } from "@/hooks/use-auth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { Button } from "@/components/ui/button";
import { MarkdownMessage } from "@/components/MarkdownMessage";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import {
  Stethoscope,
  UserRound,
  LayoutDashboard,
  FileText,
  MessageSquare,
  LogOut,
  Plus,
  Send,
  Loader2,
  Trash2,
  Sparkles,
  Check,
  Copy,
  ChevronDown,
  ChevronRight,
  Globe,
  Bot,
  Pencil,
  MoreVertical,
  MoreHorizontal,
  History,
  RotateCcw,
  Library,
  Settings as SettingsIcon,
  ArrowLeft,
  PanelLeftClose,
  PanelLeftOpen,
  FileText as FileTextIcon,
  Layers,
  Shield,
  X as XIcon,
} from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SEO } from "@/components/SEO";
import { STARTER_AGENTS, type StarterAgent } from "@shared/starterAgents";
import { normalizeTier } from "@shared/llmProviders";
import { SectionAssistant, SectionAssistantHeader } from "@/components/SectionAssistant";
import { LearnFromEditsIndicator } from "@/components/LearnFromEditsIndicator";

type ModelOption = {
  id: string;
  provider: string;
  label: string;
};

type ModelsConfigData = {
  tiers: { thinking: string; small: string };
  defaultTier: string;
};

const TIER_MODEL_OPTIONS: ModelOption[] = [
  { id: "thinking", provider: "", label: "Thinking" },
  { id: "small", provider: "", label: "Small" },
];

const FALLBACK_DEFAULT_MODEL_ID = "thinking";
const MODEL_STORAGE_KEY = "lrscribe.chat.modelId";

type ChatDoc = {
  _id: Id<"chats">;
  title: string;
  createdAt: number;
  updatedAt: number;
  agentId?: Id<"agents"> | null;
  agentName?: string | null;
  agentDeleted?: boolean;
  model?: string | null;
  kind?: string;
  sessionId?: Id<"recordingSessions">;
  templateId?: Id<"templates">;
  sectionId?: string;
  sectionTitle?: string;
  focusQuote?: string;
};

type AgentDoc = {
  _id: Id<"agents">;
  name: string;
  description?: string;
  systemPrompt: string;
  model?: string | null;
  createdAt: number;
  updatedAt: number;
};

function modelLabelFor(
  modelId: string | null | undefined,
  models: ModelOption[] = TIER_MODEL_OPTIONS
): string | null {
  if (!modelId) return null;
  // Interpret any stored (possibly legacy) model ID as a tier so old
  // chats/agents render the correct tier label instead of a raw model string.
  const tier = normalizeTier(modelId);
  const opt = models.find((m) => m.id === tier);
  return opt ? opt.label : tier;
}

type DiffLine = { type: "same" | "del" | "add"; text: string };

function diffLines(oldText: string, newText: string): {
  left: (DiffLine | null)[];
  right: (DiffLine | null)[];
} {
  const a = oldText.split("\n");
  const b = newText.split("\n");
  const m = a.length;
  const n = b.length;
  // Build LCS table
  const lcs: number[][] = Array.from({ length: m + 1 }, () =>
    new Array(n + 1).fill(0)
  );
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (a[i] === b[j]) lcs[i][j] = lcs[i + 1][j + 1] + 1;
      else lcs[i][j] = Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const left: (DiffLine | null)[] = [];
  const right: (DiffLine | null)[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      left.push({ type: "same", text: a[i] });
      right.push({ type: "same", text: b[j] });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      left.push({ type: "del", text: a[i] });
      right.push(null);
      i++;
    } else {
      left.push(null);
      right.push({ type: "add", text: b[j] });
      j++;
    }
  }
  while (i < m) {
    left.push({ type: "del", text: a[i++] });
    right.push(null);
  }
  while (j < n) {
    left.push(null);
    right.push({ type: "add", text: b[j++] });
  }
  return { left, right };
}

function groupChats(chats: ChatDoc[]) {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  ).getTime();
  const weekAgo = startOfToday - 7 * 24 * 60 * 60 * 1000;

  const today: ChatDoc[] = [];
  const recent: ChatDoc[] = [];
  const olderAll: ChatDoc[] = [];

  for (const c of chats) {
    if (c.updatedAt >= startOfToday) today.push(c);
    else if (c.updatedAt >= weekAgo) recent.push(c);
    else olderAll.push(c);
  }
  const older = olderAll.slice(0, 25);
  return { today, recent, older };
}

function CopyMessageButton({ messageId, content }: { messageId: string; content: string }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: "Copy failed", description: "Could not copy message to clipboard.", variant: "destructive" });
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-1.5 py-0.5 rounded"
      aria-label={copied ? "Copied" : "Copy message"}
      data-testid={`button-copy-message-${messageId}`}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

export default function ChatDashboard() {
  const { user, logout } = useAuth();
  const { toast } = useToast();

  const [userSynced, setUserSynced] = useState(false);
  const getOrCreateUser = useMutation(api.users.getOrCreateUser);

  useEffect(() => {
    if (user && !userSynced) {
      getOrCreateUser({
        googleId: user.id,
        email: user.email || "",
        name: user.firstName
          ? `${user.firstName} ${user.lastName || ""}`.trim()
          : undefined,
        profileImageUrl: user.profileImageUrl || undefined,
      })
        .then(() => setUserSynced(true))
        .catch((err) => console.error("Failed to sync user with Convex:", err));
    }
  }, [user, userSynced, getOrCreateUser]);

  const convexUser = useQuery(
    api.users.getCurrentUser,
    user && userSynced ? { googleId: user.id } : "skip"
  );
  const isAdmin = useIsAdmin();
  const cleanupEmptyDrafts = useMutation(api.chats.cleanupEmptyDrafts);
  const didCleanupDraftsRef = useRef(false);
  useEffect(() => {
    convexUserRef.current = convexUser?._id ?? null;
    if (convexUser?._id && !didCleanupDraftsRef.current) {
      didCleanupDraftsRef.current = true;
      cleanupEmptyDrafts({
        userId: convexUser._id,
        exceptId: draftChatIdRef.current ?? undefined,
      }).catch((err) => {
        console.warn("Failed to clean up empty draft chats:", err);
      });
    }
  }, [convexUser, cleanupEmptyDrafts]);

  const params = useParams<{ chatId?: string }>();
  const urlChatId = params?.chatId || null;
  const [, navigate] = useLocation();

  const [chatGroupExpanded, setChatGroupExpanded] = useState<{
    today: boolean;
    recent: boolean;
    older: boolean;
  }>({ today: true, recent: true, older: true });
  const [selectedChatId, setSelectedChatIdState] = useState<Id<"chats"> | null>(
    urlChatId ? (urlChatId as Id<"chats">) : null
  );
  const [pendingFocusQuote, setPendingFocusQuote] = useState<string | null>(null);
  const [draftChatId, setDraftChatId] = useState<Id<"chats"> | null>(null);
  const draftChatIdRef = useRef<Id<"chats"> | null>(null);
  const convexUserRef = useRef<Id<"users"> | null>(null);
  const removeChatRef = useRef<
    (args: { id: Id<"chats">; userId: Id<"users"> }) => Promise<unknown>
  >(async () => undefined);
  useEffect(() => {
    draftChatIdRef.current = draftChatId;
  }, [draftChatId]);
  useEffect(() => {
    return () => {
      const id = draftChatIdRef.current;
      const uid = convexUserRef.current;
      if (id && uid) {
        removeChatRef.current({ id, userId: uid }).catch(() => {});
      }
    };
  }, []);

  const setSelectedChatId = (id: Id<"chats"> | null) => {
    const prevDraft = draftChatIdRef.current;
    if (prevDraft && prevDraft !== id) {
      setDraftChatId(null);
      draftChatIdRef.current = null;
      if (convexUserRef.current) {
        removeChatRef
          .current({ id: prevDraft, userId: convexUserRef.current })
          .catch(() => {});
      }
    }
    setSelectedChatIdState(id);
    const targetPath = id ? `/chat/${id}` : "/chat";
    if (window.location.pathname !== targetPath) {
      navigate(targetPath);
    }
  };

  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem("lrscribe.pendingSectionChat");
      if (raw) {
        window.sessionStorage.removeItem("lrscribe.pendingSectionChat");
        const parsed = JSON.parse(raw) as { chatId?: string; initialFocusQuote?: string | null };
        if (parsed?.chatId) {
          setSelectedChatId(parsed.chatId as Id<"chats">);
          setPendingFocusQuote(parsed.initialFocusQuote || null);
        }
      }
    } catch {}
    try {
      const raw = window.sessionStorage.getItem("lrscribe.pendingSentenceChat");
      if (raw) {
        window.sessionStorage.removeItem("lrscribe.pendingSentenceChat");
        const parsed = JSON.parse(raw) as { chatId?: string };
        if (parsed?.chatId) {
          setSelectedChatId(parsed.chatId as Id<"chats">);
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync selection with URL changes (back/forward navigation)
  useEffect(() => {
    const urlId = urlChatId ? (urlChatId as Id<"chats">) : null;
    if (urlId !== selectedChatId) {
      setSelectedChatIdState(urlId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlChatId]);
  const [input, setInput] = useState("");
  const [streamingContent, setStreamingContent] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  // Track whether the user had an explicit model stored in localStorage at page load.
  // Used so we can apply the admin default for users with no stored preference.
  const hadStoredPreferenceRef = useRef<boolean>(
    typeof window !== "undefined" && !!(window.localStorage.getItem(MODEL_STORAGE_KEY)?.trim())
  );
  const [globalModelId, setGlobalModelId] = useState<string>(() => {
    if (typeof window === "undefined") return FALLBACK_DEFAULT_MODEL_ID;
    const saved = window.localStorage.getItem(MODEL_STORAGE_KEY);
    if (saved && saved.trim()) return normalizeTier(saved);
    return FALLBACK_DEFAULT_MODEL_ID;
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(MODEL_STORAGE_KEY, globalModelId);
    } catch {}
  }, [globalModelId]);

  const { data: modelsConfigData } = useApiQuery<ModelsConfigData>({
    queryKey: ["/api/models/config"],
  });

  // Once config loads: if the user has no explicit stored preference, apply admin default tier.
  useEffect(() => {
    if (!modelsConfigData) return;
    const adminDefault = (modelsConfigData.defaultTier ?? "thinking") as string;
    const VALID_TIERS = new Set(["thinking", "small"]);
    if (!hadStoredPreferenceRef.current || !VALID_TIERS.has(globalModelId)) {
      setGlobalModelId(adminDefault);
      hadStoredPreferenceRef.current = true;
    }
  }, [modelsConfigData]); // eslint-disable-line react-hooks/exhaustive-deps
  const [leftPaneCollapsed, setLeftPaneCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem("chat-left-pane-collapsed") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "chat-left-pane-collapsed",
        leftPaneCollapsed ? "1" : "0"
      );
    } catch {}
  }, [leftPaneCollapsed]);
  const [pendingAgentId, setPendingAgentId] = useState<Id<"agents"> | null>(
    null
  );
  const [chatsAwaitingTitle, setChatsAwaitingTitle] = useState<
    Set<Id<"chats">>
  >(new Set());

  // Agent dialog state
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<AgentDoc | null>(null);
  const [agentFormName, setAgentFormName] = useState("");
  const [agentFormDescription, setAgentFormDescription] = useState("");
  const [agentFormPrompt, setAgentFormPrompt] = useState("");
  const [agentFormModel, setAgentFormModel] = useState<string>(FALLBACK_DEFAULT_MODEL_ID);
  const [agentSaving, setAgentSaving] = useState(false);
  const [deleteAgentId, setDeleteAgentId] = useState<Id<"agents"> | null>(null);

  // History panel state
  const [historyAgentId, setHistoryAgentId] = useState<Id<"agents"> | null>(
    null
  );
  const [selectedVersionId, setSelectedVersionId] =
    useState<Id<"agentVersions"> | null>(null);
  const [restoringVersion, setRestoringVersion] = useState(false);

  // Starter agent library state
  const [starterDialogOpen, setStarterDialogOpen] = useState(false);
  const [expandedStarterId, setExpandedStarterId] = useState<string | null>(
    null
  );
  const [cloningStarterId, setCloningStarterId] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  const chats = useQuery(
    api.chats.list,
    convexUser ? { userId: convexUser._id } : "skip"
  );
  const agents = useQuery(
    api.agents.list,
    convexUser ? { userId: convexUser._id } : "skip"
  );
  const messages = useQuery(
    api.chats.getMessages,
    selectedChatId && convexUser
      ? { chatId: selectedChatId, userId: convexUser._id }
      : "skip"
  );
  const agentVersions = useQuery(
    api.agents.listVersions,
    historyAgentId && convexUser
      ? { agentId: historyAgentId, userId: convexUser._id }
      : "skip"
  );
  const userTemplates = useQuery(
    api.templates.list,
    convexUser ? { userId: convexUser._id } : "skip"
  );
  const userSessions = useQuery(
    api.recordingSessions.list,
    convexUser ? { userId: convexUser._id } : "skip"
  );
  const userRules = useQuery(
    api.globalRules.list,
    convexUser ? { userId: convexUser._id } : "skip"
  );
  const chatAttachments = useQuery(
    api.chats.getAttachments,
    selectedChatId && convexUser
      ? { id: selectedChatId, userId: convexUser._id }
      : "skip"
  );

  // Enrich tier options with the active provider-resolved model IDs as sublabels.
  const modelOptions: ModelOption[] = TIER_MODEL_OPTIONS.map((t) => ({
    ...t,
    provider:
      t.id === "thinking"
        ? (modelsConfigData?.tiers?.thinking ?? "")
        : (modelsConfigData?.tiers?.small ?? ""),
  }));
  const effectiveDefaultModelId =
    (modelsConfigData?.defaultTier ?? FALLBACK_DEFAULT_MODEL_ID) as string;

  const attachTemplateMutation = useMutation(api.chats.attachTemplate);
  const detachTemplateMutation = useMutation(api.chats.detachTemplate);
  const attachDocumentMutation = useMutation(api.chats.attachDocument);
  const detachDocumentMutation = useMutation(api.chats.detachDocument);
  const attachRuleMutation = useMutation(api.chats.attachRule);
  const detachRuleMutation = useMutation(api.chats.detachRule);

  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [documentPickerOpen, setDocumentPickerOpen] = useState(false);
  const [rulePickerOpen, setRulePickerOpen] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [documentSearch, setDocumentSearch] = useState("");
  const [ruleSearch, setRuleSearch] = useState("");
  const [docPickerSessionId, setDocPickerSessionId] =
    useState<Id<"recordingSessions"> | null>(null);

  const createChat = useMutation(api.chats.create);
  const addMessage = useMutation(api.chats.addMessage);
  const updateTitle = useMutation(api.chats.updateTitle);
  const removeChat = useMutation(api.chats.remove);
  useEffect(() => {
    removeChatRef.current = removeChat;
  }, [removeChat]);
  const createAgent = useMutation(api.agents.create);
  const updateAgent = useMutation(api.agents.update);
  const removeAgent = useMutation(api.agents.remove);
  const setChatModelMutation = useMutation(api.chats.setModel);

  const grouped = useMemo(
    () => (chats ? groupChats(chats as ChatDoc[]) : { today: [], recent: [], older: [] }),
    [chats]
  );

  const selectedChat = useMemo(
    () =>
      (chats as ChatDoc[] | undefined)?.find((c) => c._id === selectedChatId) ||
      null,
    [chats, selectedChatId]
  );

  // If the URL points to a chat that isn't in the user's list (deleted, or
  // belongs to another user), fall back to the empty /chat view. Wait until
  // chats have actually loaded before doing this.
  useEffect(() => {
    if (!selectedChatId) return;
    if (!chats) return;
    const exists = (chats as ChatDoc[]).some((c) => c._id === selectedChatId);
    if (!exists) {
      setSelectedChatIdState(null);
      if (window.location.pathname !== "/chat") {
        navigate("/chat");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats, selectedChatId]);

  const pendingAgent = useMemo(
    () =>
      (agents as AgentDoc[] | undefined)?.find((a) => a._id === pendingAgentId) ||
      null,
    [agents, pendingAgentId]
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamingContent]);

  const selectedChatAgent = useMemo(() => {
    if (!selectedChat?.agentId) return null;
    return (
      (agents as AgentDoc[] | undefined)?.find(
        (a) => a._id === selectedChat.agentId
      ) || null
    );
  }, [agents, selectedChat?.agentId]);

  const isAgentBackedChat = !!(selectedChatId && selectedChat?.agentId);
  const isSectionChat = selectedChat?.kind === "section-assistant";
  const isSentenceChat = selectedChat?.kind === "sentence-discussion";

  const effectiveModelId = useMemo(() => {
    if (isAgentBackedChat) {
      // Normalize any stored (possibly legacy) model ID to a tier so old
      // chats/agents keep working: legacy Opus -> "thinking", else -> "small".
      // A per-chat model overrides the agent's; both win over the global default.
      if (selectedChat?.model) {
        return normalizeTier(selectedChat.model);
      }
      if (selectedChatAgent?.model) {
        return normalizeTier(selectedChatAgent.model);
      }
    }
    return normalizeTier(globalModelId);
  }, [
    isAgentBackedChat,
    selectedChat?.model,
    selectedChatAgent?.model,
    globalModelId,
  ]);

  const selectedModel =
    modelOptions.find((m) => m.id === effectiveModelId) || modelOptions[0];

  async function handleSelectModel(nextId: string) {
    if (nextId === effectiveModelId) return;
    if (selectedChatId && selectedChat?.agentId && convexUser) {
      try {
        await setChatModelMutation({
          id: selectedChatId,
          userId: convexUser._id,
          model: nextId,
        });
      } catch (e: any) {
        toast({
          title: "Couldn't save model for this chat",
          description: e?.message,
          variant: "destructive",
        });
      }
    } else {
      setGlobalModelId(nextId);
    }
  }

  const agentModelById = useMemo(() => {
    const map = new Map<string, string | null | undefined>();
    for (const a of (agents as AgentDoc[] | undefined) || []) {
      map.set(String(a._id), a.model);
    }
    return map;
  }, [agents]);

  if (!user || !convexUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  function openCreateAgentDialog() {
    setEditingAgent(null);
    setAgentFormName("");
    setAgentFormDescription("");
    setAgentFormPrompt("");
    setAgentFormModel(globalModelId || effectiveDefaultModelId);
    setAgentDialogOpen(true);
  }

  function openEditAgentDialog(agent: AgentDoc) {
    setEditingAgent(agent);
    setAgentFormName(agent.name);
    setAgentFormDescription(agent.description || "");
    setAgentFormPrompt(agent.systemPrompt);
    setAgentFormModel(
      agent.model
        ? normalizeTier(agent.model)
        : globalModelId || effectiveDefaultModelId
    );
    setAgentDialogOpen(true);
  }

  async function handleSaveAgent() {
    if (!convexUser) return;
    const name = agentFormName.trim();
    const prompt = agentFormPrompt.trim();
    if (!name) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    if (!prompt) {
      toast({ title: "System prompt is required", variant: "destructive" });
      return;
    }
    setAgentSaving(true);
    try {
      const modelValue = agentFormModel
        ? normalizeTier(agentFormModel)
        : effectiveDefaultModelId;
      if (editingAgent) {
        await updateAgent({
          id: editingAgent._id,
          userId: convexUser._id,
          name,
          description: agentFormDescription.trim() || undefined,
          systemPrompt: prompt,
          model: modelValue,
        });
      } else {
        await createAgent({
          userId: convexUser._id,
          name,
          description: agentFormDescription.trim() || undefined,
          systemPrompt: prompt,
          model: modelValue,
        });
      }
      setAgentDialogOpen(false);
    } catch (e: any) {
      toast({
        title: "Save failed",
        description: e?.message,
        variant: "destructive",
      });
    } finally {
      setAgentSaving(false);
    }
  }

  function openHistoryForAgent(agentId: Id<"agents">) {
    setHistoryAgentId(agentId);
    setSelectedVersionId(null);
  }

  async function handleRestoreVersion() {
    if (!convexUser || !historyAgentId || !selectedVersionId) return;
    const versions: Doc<"agentVersions">[] = agentVersions || [];
    const version = versions.find((v) => v._id === selectedVersionId);
    if (!version) return;
    setRestoringVersion(true);
    try {
      await updateAgent({
        id: historyAgentId,
        userId: convexUser._id,
        name: version.name,
        description: version.description || undefined,
        systemPrompt: version.systemPrompt,
        model: version.model || undefined,
      });
      // Refresh the agent edit dialog form if it's open for this agent
      if (editingAgent && editingAgent._id === historyAgentId) {
        setAgentFormName(version.name);
        setAgentFormDescription(version.description || "");
        setAgentFormPrompt(version.systemPrompt);
        setAgentFormModel(
          version.model
            ? normalizeTier(version.model)
            : effectiveDefaultModelId
        );
      }
      toast({ title: "Version restored" });
      setSelectedVersionId(null);
    } catch (e: unknown) {
      toast({
        title: "Restore failed",
        description: e instanceof Error ? e.message : undefined,
        variant: "destructive",
      });
    } finally {
      setRestoringVersion(false);
    }
  }

  async function handleConfirmDeleteAgent() {
    if (!convexUser || !deleteAgentId) return;
    try {
      await removeAgent({ id: deleteAgentId, userId: convexUser._id });
      setDeleteAgentId(null);
    } catch (e: any) {
      toast({
        title: "Delete failed",
        description: e?.message,
        variant: "destructive",
      });
    }
  }

  async function handleCloneStarterAgent(starter: StarterAgent) {
    if (!convexUser) return;
    setCloningStarterId(starter.id);
    try {
      const modelValue = starter.defaultModel
        ? normalizeTier(starter.defaultModel)
        : undefined;
      const existingNames = new Set(
        ((agents as AgentDoc[] | undefined) || []).map((a) => a.name)
      );
      let name = starter.name;
      if (existingNames.has(name)) {
        let i = 2;
        while (existingNames.has(`${starter.name} (${i})`)) i++;
        name = `${starter.name} (${i})`;
      }
      const newAgentId = await createAgent({
        userId: convexUser._id,
        name,
        description: starter.description,
        systemPrompt: starter.systemPrompt,
        model: modelValue,
      });
      setStarterDialogOpen(false);
      toast({
        title: "Agent added",
        description: `"${name}" is ready — starting a new chat.`,
      });
      await handleStartChatWithAgent(newAgentId);
    } catch (e: any) {
      toast({
        title: "Couldn't add starter agent",
        description: e?.message,
        variant: "destructive",
      });
    } finally {
      setCloningStarterId(null);
    }
  }

  async function handleStartChatWithAgent(agentId: Id<"agents">) {
    if (!convexUser) return;
    try {
      const agent = (agents as AgentDoc[] | undefined)?.find(
        (a) => a._id === agentId
      );
      const agentModel = agent?.model
        ? normalizeTier(agent.model)
        : undefined;
      const chatId = await createChat({
        userId: convexUser._id,
        title: "New Chat",
        agentId,
        model: agentModel,
      });
      setChatsAwaitingTitle((prev) => {
        const next = new Set(prev);
        next.add(chatId);
        return next;
      });
      setSelectedChatId(chatId);
      setPendingAgentId(null);
      setInput("");
    } catch (e: any) {
      toast({
        title: "Failed to start chat",
        description: e?.message,
        variant: "destructive",
      });
    }
  }

  async function ensureChatForAttachments(): Promise<Id<"chats"> | null> {
    if (selectedChatId) return selectedChatId;
    if (!convexUser) return null;
    try {
      const pendingAgentDoc = pendingAgentId
        ? (agents as AgentDoc[] | undefined)?.find(
            (a) => a._id === pendingAgentId
          )
        : null;
      const agentModel = pendingAgentDoc?.model
        ? normalizeTier(pendingAgentDoc.model)
        : undefined;
      const initialModel = pendingAgentId
        ? agentModel || globalModelId
        : undefined;
      const chatId = await createChat({
        userId: convexUser._id,
        title: "New Chat",
        agentId: pendingAgentId || undefined,
        model: initialModel,
      });
      setChatsAwaitingTitle((prev) => {
        const next = new Set(prev);
        next.add(chatId);
        return next;
      });
      setDraftChatId(chatId);
      draftChatIdRef.current = chatId;
      setSelectedChatIdState(chatId);
      const targetPath = `/chat/${chatId}`;
      if (window.location.pathname !== targetPath) {
        navigate(targetPath);
      }
      return chatId;
    } catch (e: any) {
      toast({
        title: "Couldn't start chat",
        description: e?.message,
        variant: "destructive",
      });
      return null;
    }
  }

  async function handleSend() {
    const text = input.trim();
    if (!text || isStreaming || !convexUser) return;

    let chatId = selectedChatId;
    let needsTitle = false;
    if (chatId && draftChatId === chatId) {
      setDraftChatId(null);
      draftChatIdRef.current = null;
      setPendingAgentId(null);
    }
    if (!chatId) {
      const title = text.length > 50 ? text.slice(0, 50) + "…" : text;
      const pendingAgentDoc = pendingAgentId
        ? (agents as AgentDoc[] | undefined)?.find(
            (a) => a._id === pendingAgentId
          )
        : null;
      const agentModel = pendingAgentDoc?.model
        ? normalizeTier(pendingAgentDoc.model)
        : undefined;
      const initialModel = pendingAgentId
        ? agentModel || globalModelId
        : undefined;
      chatId = await createChat({
        userId: convexUser._id,
        title,
        agentId: pendingAgentId || undefined,
        model: initialModel,
      });
      setSelectedChatId(chatId);
      setPendingAgentId(null);
      needsTitle = true;
    } else if (chatsAwaitingTitle.has(chatId)) {
      needsTitle = true;
    }

    const priorMessages = (messages || []).map((m) => ({
      role: m.role,
      content: m.content,
    }));

    await addMessage({
      chatId,
      userId: convexUser._id,
      role: "user",
      content: text,
    });
    setInput("");
    setIsStreaming(true);
    setStreamingContent("");

    try {
      const res = await fetch("/api/chat/stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          messages: [...priorMessages, { role: "user", content: text }],
          model: effectiveModelId,
          chatId,
        }),
      });

      if (!res.ok || !res.body) {
        throw new Error(`Stream failed: ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";

      let streamError: string | null = null;
      outer: while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const json = line.slice(5).trim();
          let evt: any;
          try {
            evt = JSON.parse(json);
          } catch {
            continue;
          }
          if (evt.error) {
            streamError = evt.error;
            break outer;
          }
          if (evt.content) {
            full += evt.content;
            setStreamingContent(full);
          }
        }
      }

      if (streamError) {
        throw new Error(streamError);
      }
      if (!full) {
        throw new Error("Empty response from model");
      }

      await addMessage({
        chatId,
        userId: convexUser._id,
        role: "assistant",
        content: full,
      });

      if (needsTitle) {
        await updateTitle({
          id: chatId,
          userId: convexUser._id,
          title: text.length > 50 ? text.slice(0, 50) + "…" : text,
        });
        setChatsAwaitingTitle((prev) => {
          if (!prev.has(chatId!)) return prev;
          const next = new Set(prev);
          next.delete(chatId!);
          return next;
        });
      }
    } catch (err: any) {
      toast({
        title: "Chat error",
        description: err?.message || "Failed to get response",
        variant: "destructive",
      });
    } finally {
      setIsStreaming(false);
      setStreamingContent("");
    }
  }

  async function handleDelete(id: Id<"chats">) {
    if (!convexUser) return;
    try {
      await removeChat({ id, userId: convexUser._id });
      if (selectedChatId === id) setSelectedChatId(null);
    } catch (e: any) {
      toast({
        title: "Delete failed",
        description: e?.message,
        variant: "destructive",
      });
    }
  }

  const chatEffectiveModelLabel = (c: ChatDoc): string | null => {
    if (c.model) {
      const label = modelLabelFor(c.model, modelOptions);
      if (label) return label;
    }
    if (c.agentId && !c.agentDeleted) {
      const agentModel = agentModelById.get(String(c.agentId));
      const label = modelLabelFor(agentModel, modelOptions);
      if (label) return label;
    }
    return modelLabelFor(effectiveDefaultModelId, modelOptions);
  };

  const renderGroup = (label: string, list: ChatDoc[], groupKey: "today" | "recent" | "older") => {
    if (list.length === 0) return null;
    const expanded = chatGroupExpanded[groupKey];
    const testIdSuffix = groupKey === "today" ? "today" : groupKey === "recent" ? "last-week" : "older";
    return (
      <div className="mb-3">
        <button
          type="button"
          onClick={() =>
            setChatGroupExpanded((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))
          }
          className="w-full flex items-center gap-1 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover-elevate active-elevate-2 rounded-md"
          data-testid={`button-toggle-chat-group-${testIdSuffix}`}
        >
          {expanded ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3" />
          )}
          <span>{label}</span>
        </button>
        {expanded && list.map((c) => {
          const modelLabel = chatEffectiveModelLabel(c);
          return (
          <div
            key={c._id}
            className={`group flex items-center justify-between gap-1 px-3 py-2 rounded-md mx-2 cursor-pointer text-sm ${
              selectedChatId === c._id
                ? "bg-primary/10 text-primary"
                : "hover:bg-muted text-foreground"
            }`}
            onClick={() => setSelectedChatId(c._id)}
            data-testid={`chat-item-${c._id}`}
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {c.kind === "section-assistant" ? (
                <Sparkles className="w-4 h-4 shrink-0 text-primary" data-testid={`icon-section-chat-${c._id}`} />
              ) : (
                <MessageSquare className="w-4 h-4 shrink-0" />
              )}
              <div className="flex flex-col min-w-0 flex-1">
                <span className="truncate">{c.title}</span>
                {c.kind === "section-assistant" && c.sectionTitle && (
                  <span
                    className="text-[10px] truncate text-muted-foreground"
                    data-testid={`chat-section-badge-${c._id}`}
                  >
                    Section: {c.sectionTitle}
                  </span>
                )}
                {(c.agentName || c.agentDeleted) && (
                  <span
                    className={`text-[10px] truncate flex items-center gap-1 ${
                      c.agentDeleted
                        ? "text-muted-foreground italic"
                        : "text-muted-foreground"
                    }`}
                    data-testid={`chat-agent-badge-${c._id}`}
                  >
                    <Bot className="w-2.5 h-2.5 shrink-0" />
                    {c.agentDeleted ? "Agent deleted" : c.agentName}
                  </span>
                )}
                {modelLabel && (
                  <span
                    className="text-[10px] truncate text-muted-foreground"
                    data-testid={`chat-model-badge-${c._id}`}
                  >
                    {modelLabel}
                  </span>
                )}
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="shrink-0 ml-1 text-muted-foreground hover:text-foreground p-1 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="Open chat menu"
                  data-testid={`button-chat-menu-${c._id}`}
                >
                  <MoreHorizontal className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                onClick={(e) => e.stopPropagation()}
              >
                <DropdownMenuItem
                  onSelect={() => handleDelete(c._id)}
                  className="text-destructive focus:text-destructive"
                  data-testid={`button-delete-chat-${c._id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete chat
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          );
        })}
      </div>
    );
  };

  const hasMessages = (messages && messages.length > 0) || isStreaming;
  const agentsList = (agents as AgentDoc[] | undefined) || [];

  function getDocumentSectionTitles(doc: string): string[] {
    const titles: string[] = [];
    const re = /^#{1,6}\s+(.+?)\s*$/gm;
    let m: RegExpExecArray | null;
    while ((m = re.exec(doc)) !== null) {
      const t = m[1].trim();
      if (t) titles.push(t);
    }
    return titles;
  }

  function sessionLabel(s: Doc<"recordingSessions">): string {
    return (
      s.title?.trim() ||
      s.patientName?.trim() ||
      (s.recordingDate
        ? new Date(s.recordingDate).toLocaleDateString()
        : "Session")
    );
  }

  type AttachmentsView = NonNullable<typeof chatAttachments>;
  const emptyAttachments: AttachmentsView = {
    templates: [],
    documents: [],
    rules: [],
  };

  const renderChip = (params: {
    key: string;
    label: string;
    deleted: boolean;
    testId: string;
    onRemove: () => void;
  }) => (
    <div
      key={params.key}
      className={`inline-flex items-center gap-1 max-w-full rounded-full border px-2 py-0.5 text-xs ${
        params.deleted
          ? "border-muted-foreground/30 bg-muted text-muted-foreground"
          : "border-border bg-background text-foreground"
      }`}
      data-testid={params.testId}
    >
      <span className="truncate" title={params.label}>
        {params.deleted ? `${params.label || "Item"} (deleted)` : params.label}
      </span>
      <button
        type="button"
        onClick={params.onRemove}
        className="opacity-60 hover:opacity-100 shrink-0"
        aria-label="Remove attachment"
        data-testid={`${params.testId}-remove`}
      >
        <XIcon className="w-3 h-3" />
      </button>
    </div>
  );

  const renderAttachmentsSection = () => {
    const att = chatAttachments || emptyAttachments;
    const attachedTemplateIdSet = new Set(
      att.templates.map((t) => String(t.templateId)),
    );
    const attachedRuleIdSet = new Set(
      att.rules.map((r) => String(r.ruleId)),
    );
    const attachedDocKey = (sessionId: string, sectionTitle?: string) =>
      `${sessionId}::${sectionTitle || ""}`;
    const attachedDocKeySet = new Set(
      att.documents.map((d) =>
        attachedDocKey(String(d.sessionId), d.sectionTitle),
      ),
    );

    const filteredTemplates = (userTemplates || []).filter((t) =>
      t.name.toLowerCase().includes(templateSearch.toLowerCase()),
    );
    const filteredSessions = (userSessions || [])
      .filter((s) => !!s.generatedDocument)
      .filter((s) =>
        sessionLabel(s).toLowerCase().includes(documentSearch.toLowerCase()),
      );
    const filteredRules = (userRules || []).filter((r) =>
      r.content.toLowerCase().includes(ruleSearch.toLowerCase()),
    );

    const docPickerSession =
      docPickerSessionId !== null
        ? (userSessions || []).find((s) => s._id === docPickerSessionId) ||
          null
        : null;
    const docSectionTitles = docPickerSession?.generatedDocument
      ? getDocumentSectionTitles(docPickerSession.generatedDocument)
      : [];

    return (
      <>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Attachments
        </div>

        {/* Templates */}
        <div className="space-y-1.5">
          <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/80">
            Templates
          </div>
          <Popover
            open={templatePickerOpen}
            onOpenChange={(o) => {
              setTemplatePickerOpen(o);
              if (!o) setTemplateSearch("");
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-1.5 h-8"
                data-testid="button-add-template-attachment"
              >
                <Layers className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-xs">Add template</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-2">
              <Input
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                placeholder="Search templates…"
                className="h-8 text-xs mb-2"
                data-testid="input-search-template-attachment"
              />
              <div className="max-h-64 overflow-y-auto">
                {filteredTemplates.length === 0 ? (
                  <div className="text-xs text-muted-foreground px-2 py-3 text-center">
                    No templates
                  </div>
                ) : (
                  filteredTemplates.map((t) => {
                    const already = attachedTemplateIdSet.has(String(t._id));
                    return (
                      <button
                        type="button"
                        key={t._id}
                        disabled={already}
                        onClick={async () => {
                          if (!convexUser) return;
                          const cid = await ensureChatForAttachments();
                          if (!cid) return;
                          try {
                            await attachTemplateMutation({
                              id: cid,
                              userId: convexUser._id,
                              templateId: t._id,
                            });
                            setTemplatePickerOpen(false);
                            setTemplateSearch("");
                          } catch (e: any) {
                            toast({
                              title: "Couldn't attach template",
                              description: e?.message,
                              variant: "destructive",
                            });
                          }
                        }}
                        className={`w-full text-left text-xs px-2 py-1.5 rounded-md flex items-center justify-between gap-2 ${
                          already
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:bg-muted"
                        }`}
                        data-testid={`option-attach-template-${t._id}`}
                      >
                        <span className="truncate">{t.name}</span>
                        {already && (
                          <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </PopoverContent>
          </Popover>
          {att.templates.length > 0 && (
            <div
              className="flex flex-wrap gap-1"
              data-testid="list-attached-templates"
            >
              {att.templates.map((t) =>
                renderChip({
                  key: String(t.templateId),
                  label: t.name || "Template",
                  deleted: t.deleted,
                  testId: `chip-attached-template-${t.templateId}`,
                  onRemove: async () => {
                    if (!convexUser || !selectedChatId) return;
                    try {
                      await detachTemplateMutation({
                        id: selectedChatId,
                        userId: convexUser._id,
                        templateId: t.templateId,
                      });
                    } catch (e: any) {
                      toast({
                        title: "Couldn't remove attachment",
                        description: e?.message,
                        variant: "destructive",
                      });
                    }
                  },
                }),
              )}
            </div>
          )}
        </div>

        {/* Documents & sections */}
        <div className="space-y-1.5">
          <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/80">
            Documents & sections
          </div>
          <Popover
            open={documentPickerOpen}
            onOpenChange={(o) => {
              setDocumentPickerOpen(o);
              if (!o) {
                setDocumentSearch("");
                setDocPickerSessionId(null);
              }
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-1.5 h-8"
                data-testid="button-add-document-attachment"
              >
                <FileTextIcon className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-xs">Add document / section</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-2">
              {docPickerSession ? (
                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-1.5 text-xs"
                      onClick={() => setDocPickerSessionId(null)}
                      data-testid="button-back-document-picker"
                    >
                      <ArrowLeft className="w-3 h-3 mr-1" />
                      Back
                    </Button>
                    <span className="text-xs font-medium truncate">
                      {sessionLabel(docPickerSession)}
                    </span>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-0.5">
                    {(() => {
                      const wholeKey = attachedDocKey(
                        String(docPickerSession._id),
                        undefined,
                      );
                      const already = attachedDocKeySet.has(wholeKey);
                      return (
                        <button
                          type="button"
                          disabled={already}
                          onClick={async () => {
                            if (!convexUser) return;
                            const cid = await ensureChatForAttachments();
                            if (!cid) return;
                            try {
                              await attachDocumentMutation({
                                id: cid,
                                userId: convexUser._id,
                                sessionId: docPickerSession._id,
                              });
                              setDocumentPickerOpen(false);
                              setDocPickerSessionId(null);
                              setDocumentSearch("");
                            } catch (e: any) {
                              toast({
                                title: "Couldn't attach document",
                                description: e?.message,
                                variant: "destructive",
                              });
                            }
                          }}
                          className={`w-full text-left text-xs px-2 py-1.5 rounded-md flex items-center justify-between gap-2 ${
                            already
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-muted"
                          }`}
                          data-testid="option-attach-document-whole"
                        >
                          <span className="truncate font-medium">
                            Whole document
                          </span>
                          {already && (
                            <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                          )}
                        </button>
                      );
                    })()}
                    {docSectionTitles.length === 0 ? (
                      <div className="text-[11px] text-muted-foreground px-2 py-2">
                        No headings found in this document.
                      </div>
                    ) : (
                      docSectionTitles.map((title, idx) => {
                        const k = attachedDocKey(
                          String(docPickerSession._id),
                          title,
                        );
                        const already = attachedDocKeySet.has(k);
                        return (
                          <button
                            type="button"
                            key={`${title}-${idx}`}
                            disabled={already}
                            onClick={async () => {
                              if (!convexUser) return;
                              const cid = await ensureChatForAttachments();
                              if (!cid) return;
                              try {
                                await attachDocumentMutation({
                                  id: cid,
                                  userId: convexUser._id,
                                  sessionId: docPickerSession._id,
                                  sectionTitle: title,
                                });
                                setDocumentPickerOpen(false);
                                setDocPickerSessionId(null);
                                setDocumentSearch("");
                              } catch (e: any) {
                                toast({
                                  title: "Couldn't attach section",
                                  description: e?.message,
                                  variant: "destructive",
                                });
                              }
                            }}
                            className={`w-full text-left text-xs pl-5 pr-2 py-1.5 rounded-md flex items-center justify-between gap-2 ${
                              already
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:bg-muted"
                            }`}
                            data-testid={`option-attach-document-section-${idx}`}
                          >
                            <span className="truncate">{title}</span>
                            {already && (
                              <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <Input
                    value={documentSearch}
                    onChange={(e) => setDocumentSearch(e.target.value)}
                    placeholder="Search documents…"
                    className="h-8 text-xs mb-2"
                    data-testid="input-search-document-attachment"
                  />
                  <div className="max-h-64 overflow-y-auto">
                    {filteredSessions.length === 0 ? (
                      <div className="text-xs text-muted-foreground px-2 py-3 text-center">
                        No generated documents
                      </div>
                    ) : (
                      filteredSessions.map((s) => (
                        <button
                          type="button"
                          key={s._id}
                          onClick={() => setDocPickerSessionId(s._id)}
                          className="w-full text-left text-xs px-2 py-1.5 rounded-md hover:bg-muted flex items-center justify-between gap-2"
                          data-testid={`option-pick-document-${s._id}`}
                        >
                          <span className="truncate">{sessionLabel(s)}</span>
                          <ChevronRight className="w-3.5 h-3.5 opacity-60 shrink-0" />
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
            </PopoverContent>
          </Popover>
          {att.documents.length > 0 && (
            <div
              className="flex flex-wrap gap-1"
              data-testid="list-attached-documents"
            >
              {att.documents.map((d, idx) => {
                const label = d.sectionTitle
                  ? `${d.name || "Document"} › ${d.sectionTitle}`
                  : d.name || "Document";
                return renderChip({
                  key: `${String(d.sessionId)}-${d.sectionTitle || ""}-${idx}`,
                  label,
                  deleted: d.deleted,
                  testId: `chip-attached-document-${idx}`,
                  onRemove: async () => {
                    if (!convexUser || !selectedChatId) return;
                    try {
                      await detachDocumentMutation({
                        id: selectedChatId,
                        userId: convexUser._id,
                        sessionId: d.sessionId,
                        sectionTitle: d.sectionTitle,
                      });
                    } catch (e: any) {
                      toast({
                        title: "Couldn't remove attachment",
                        description: e?.message,
                        variant: "destructive",
                      });
                    }
                  },
                });
              })}
            </div>
          )}
        </div>

        {/* Rules */}
        <div className="space-y-1.5">
          <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/80">
            Rules
          </div>
          <Popover
            open={rulePickerOpen}
            onOpenChange={(o) => {
              setRulePickerOpen(o);
              if (!o) setRuleSearch("");
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-1.5 h-8"
                data-testid="button-add-rule-attachment"
              >
                <Shield className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-xs">Add rule</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-2">
              <Input
                value={ruleSearch}
                onChange={(e) => setRuleSearch(e.target.value)}
                placeholder="Search rules…"
                className="h-8 text-xs mb-2"
                data-testid="input-search-rule-attachment"
              />
              <div className="max-h-64 overflow-y-auto">
                {filteredRules.length === 0 ? (
                  <div className="text-xs text-muted-foreground px-2 py-3 text-center">
                    No rules
                  </div>
                ) : (
                  filteredRules.map((r) => {
                    const already = attachedRuleIdSet.has(String(r._id));
                    const preview = r.content
                      .replace(/\s+/g, " ")
                      .slice(0, 80);
                    return (
                      <button
                        type="button"
                        key={r._id}
                        disabled={already}
                        onClick={async () => {
                          if (!convexUser) return;
                          const cid = await ensureChatForAttachments();
                          if (!cid) return;
                          try {
                            await attachRuleMutation({
                              id: cid,
                              userId: convexUser._id,
                              ruleId: r._id,
                            });
                            setRulePickerOpen(false);
                            setRuleSearch("");
                          } catch (e: any) {
                            toast({
                              title: "Couldn't attach rule",
                              description: e?.message,
                              variant: "destructive",
                            });
                          }
                        }}
                        className={`w-full text-left text-xs px-2 py-1.5 rounded-md flex items-center justify-between gap-2 ${
                          already
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:bg-muted"
                        }`}
                        data-testid={`option-attach-rule-${r._id}`}
                      >
                        <span className="truncate">{preview}</span>
                        {already && (
                          <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </PopoverContent>
          </Popover>
          {att.rules.length > 0 && (
            <div
              className="flex flex-wrap gap-1"
              data-testid="list-attached-rules"
            >
              {att.rules.map((r) =>
                renderChip({
                  key: String(r.ruleId),
                  label: r.name || "Rule",
                  deleted: r.deleted,
                  testId: `chip-attached-rule-${r.ruleId}`,
                  onRemove: async () => {
                    if (!convexUser || !selectedChatId) return;
                    try {
                      await detachRuleMutation({
                        id: selectedChatId,
                        userId: convexUser._id,
                        ruleId: r.ruleId,
                      });
                    } catch (e: any) {
                      toast({
                        title: "Couldn't remove attachment",
                        description: e?.message,
                        variant: "destructive",
                      });
                    }
                  },
                }),
              )}
            </div>
          )}
        </div>
      </>
    );
  };

  const renderModelSelector = () => (
    <div className="p-3 border-b border-border space-y-1.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Model
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-between gap-1.5 h-8"
            data-testid="button-model-selector"
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <Sparkles className="w-3.5 h-3.5 text-primary shrink-0" />
              <span className="text-xs font-medium truncate">
                {selectedModel?.label ?? effectiveModelId}
              </span>
            </div>
            <ChevronDown className="w-3.5 h-3.5 opacity-60 shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {modelOptions.map((opt) => {
            const isSelected = opt.id === effectiveModelId;
            return (
              <DropdownMenuItem
                key={opt.id}
                onClick={() => handleSelectModel(opt.id)}
                data-testid={`menu-model-${opt.id}`}
              >
                <div className="flex items-center justify-between w-full gap-2">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{opt.label}</span>
                    {opt.provider && (
                      <span className="text-xs text-muted-foreground">{opt.provider}</span>
                    )}
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-primary" />}
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      <div
        className="text-[10px] text-muted-foreground truncate"
        data-testid="text-model-label"
      >
        {selectedModel?.provider ?? ""}
      </div>
    </div>
  );

  const renderSectionContextSlot = () => (
    <>
      <div className="p-3 border-b border-border space-y-1">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Context
        </div>
        {selectedChat?.sectionTitle ? (
          <div
            className="flex flex-col gap-1 text-sm text-foreground min-w-0"
            data-testid="text-section-chat-header"
          >
            {selectedChat.sessionId && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 -ml-2 self-start text-muted-foreground hover:text-foreground"
                onClick={() =>
                  navigate(`/dashboard/session/${selectedChat.sessionId}`)
                }
                aria-label="Back to document"
                data-testid="button-back-to-document"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to document
              </Button>
            )}
            <div className="flex items-start gap-1.5 min-w-0">
              <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Section
                </div>
                <div className="text-sm truncate">
                  {selectedChat.sectionTitle}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {renderModelSelector()}
    </>
  );

  const headerAgentLabel = selectedChat
    ? selectedChat.agentDeleted
      ? "Agent deleted"
      : selectedChat.agentName
        ? `Agent: ${selectedChat.agentName}`
        : "No agent"
    : null;

  return (
    <TooltipProvider>
    <div className="h-screen flex flex-col bg-background">
      <SEO title="Chat — LRScribe" noindex />
      {/* Slim top header (chat-only) — chat-specific controls moved to right pane */}
      <header className="relative h-[26px] border-b border-border bg-card flex items-center justify-between px-2 shrink-0 gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Link
            href="/dashboard"
            className="flex items-center gap-1 shrink-0"
            aria-label="LRScribe home"
          >
            <div className="p-0.5 bg-primary/10 rounded-sm">
              <Stethoscope className="w-3 h-3 text-primary" />
            </div>
            <span className="font-heading font-bold text-xs text-foreground hidden sm:inline leading-none">
              LRScribe
            </span>
          </Link>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 text-muted-foreground hover:text-foreground"
                onClick={() => setLeftPaneCollapsed((v) => !v)}
                aria-label={
                  leftPaneCollapsed ? "Expand sidebar" : "Collapse sidebar"
                }
                aria-pressed={leftPaneCollapsed}
                data-testid="button-toggle-left-pane"
              >
                {leftPaneCollapsed ? (
                  <PanelLeftOpen className="w-3.5 h-3.5" />
                ) : (
                  <PanelLeftClose className="w-3.5 h-3.5" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {leftPaneCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            </TooltipContent>
          </Tooltip>
        </div>

        <nav className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3">
          <Link
            href="/dashboard"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-dashboard"
          >
            <LayoutDashboard className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
          <Link
            href="/templates"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-templates"
          >
            <FileText className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Templates</span>
          </Link>
          <Link
            href="/rules"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-rules"
          >
            <Globe className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Rules</span>
          </Link>
          <Link
            href="/profiles"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-profiles"
          >
            <UserRound className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Profiles</span>
          </Link>
          <Link
            href="/chat"
            className="text-xs font-medium text-primary transition-colors flex items-center gap-1 leading-none"
            data-testid="link-chat"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Chat</span>
          </Link>
        </nav>

        <div className="flex items-center gap-0.5 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 text-muted-foreground hover:text-foreground"
                aria-label="Open more menu"
                data-testid="button-nav-menu"
              >
                <MoreHorizontal className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem asChild>
                <Link href="/change-log" data-testid="link-change-log-nav">
                  <History className="w-4 h-4 mr-2" />
                  Change log
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <ThemeToggle className="h-5 w-5 [&_svg]:w-3.5 [&_svg]:h-3.5" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Open user menu"
                className="flex items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background cursor-pointer"
                data-testid="button-user-menu"
              >
                <Avatar className="h-5 w-5 pointer-events-none">
                  <AvatarImage src={user.profileImageUrl || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-[10px]">
                    {user.firstName?.charAt(0) || user.email?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div
                  className="text-sm font-medium text-foreground truncate"
                  data-testid="text-user-name"
                >
                  {user.firstName
                    ? `${user.firstName} ${user.lastName || ""}`.trim()
                    : user.email}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link href="/settings" data-testid="link-settings">
                  <SettingsIcon className="w-4 h-4 mr-2" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/security" data-testid="link-security">
                  <Shield className="w-4 h-4 mr-2" />
                  Security
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/change-log" data-testid="link-change-log">
                  <History className="w-4 h-4 mr-2" />
                  Change log
                </Link>
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem asChild>
                  <Link href="/admin/settings" data-testid="link-admin">
                    <Shield className="w-4 h-4 mr-2" />
                    Admin
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => logout()}
                className="text-destructive focus:text-destructive"
                data-testid="button-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Pane - Agents + Chats */}
        <aside
          className={`w-80 border-r border-border bg-card flex-col min-h-0 shrink-0 ${leftPaneCollapsed ? "hidden" : "flex"}`}
          data-testid="pane-chat-left"
          aria-hidden={leftPaneCollapsed}>
          <div className="p-4 border-b border-border space-y-2">
            <Button
              className="w-full"
              onClick={() => {
                setSelectedChatId(null);
                setPendingAgentId(null);
                setInput("");
              }}
              data-testid="button-new-chat"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Chat
            </Button>
            <Button
              variant="secondary"
              className="w-full"
              onClick={openCreateAgentDialog}
              data-testid="button-new-agent"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Agent
            </Button>
          </div>

          <ScrollArea className="flex-1">
            {/* Agents section */}
            <div className="py-3 border-b border-border">
              <div className="flex items-center justify-between px-4 pb-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Agents
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2"
                    onClick={() => {
                      setExpandedStarterId(null);
                      setStarterDialogOpen(true);
                    }}
                    data-testid="button-browse-starter-agents"
                  >
                    <Library className="w-3.5 h-3.5 mr-1" />
                    Browse
                  </Button>
                </div>
              </div>
              {agents === undefined ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : agentsList.length === 0 ? (
                <div className="px-3 pb-2" data-testid="agents-empty-starter-list">
                  <div className="text-xs text-muted-foreground px-1 pb-2">
                    Start with a curated reviewer agent, or create your own.
                  </div>
                  {STARTER_AGENTS.slice(0, 4).map((s) => (
                    <button
                      key={s.id}
                      onClick={() => handleCloneStarterAgent(s)}
                      disabled={cloningStarterId === s.id}
                      className="w-full text-left flex items-start gap-2 px-2 py-2 rounded-md hover:bg-muted disabled:opacity-60"
                      data-testid={`starter-inline-${s.id}`}
                    >
                      <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm truncate">{s.name}</div>
                        <div className="text-[11px] text-muted-foreground line-clamp-2">
                          {s.description}
                        </div>
                      </div>
                      {cloningStarterId === s.id && (
                        <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                      )}
                    </button>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-1 text-xs text-primary hover:text-primary"
                    onClick={() => {
                      setExpandedStarterId(null);
                      setStarterDialogOpen(true);
                    }}
                    data-testid="button-see-all-starters"
                  >
                    See all {STARTER_AGENTS.length} starter agents
                  </Button>
                </div>
              ) : (
                agentsList.map((a) => (
                  <div
                    key={a._id}
                    className="group flex items-center justify-between gap-1 px-3 py-2 rounded-md mx-2 cursor-pointer text-sm hover:bg-muted"
                    onClick={() => handleStartChatWithAgent(a._id)}
                    data-testid={`agent-row-${a._id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Bot className="w-4 h-4 shrink-0 text-primary" />
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="truncate" data-testid={`agent-name-${a._id}`}>
                              {a.name}
                            </span>
                            {modelLabelFor(a.model, modelOptions) && (
                              <span
                                className="text-[10px] text-muted-foreground truncate"
                                data-testid={`agent-model-${a._id}`}
                              >
                                {modelLabelFor(a.model, modelOptions)}
                              </span>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          <div className="max-w-xs">
                            <div className="font-medium">{a.name}</div>
                            {a.description && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {a.description}
                              </div>
                            )}
                            {modelLabelFor(a.model, modelOptions) && (
                              <div className="text-xs text-muted-foreground mt-1">
                                Model: {modelLabelFor(a.model, modelOptions)}
                              </div>
                            )}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity"
                          data-testid={`button-agent-menu-${a._id}`}
                        >
                          <MoreVertical className="w-3.5 h-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenuItem
                          onClick={() => openEditAgentDialog(a)}
                          data-testid={`button-edit-agent-${a._id}`}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setDeleteAgentId(a._id)}
                          data-testid={`button-delete-agent-${a._id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))
              )}
            </div>

            {/* Chats section */}
            <div className="py-3">
              <div className="px-4 pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Chats
              </div>
              {chats === undefined ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (chats as ChatDoc[]).length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8 px-4">
                  No chats yet. Start a new conversation.
                </div>
              ) : (
                <>
                  {renderGroup("Today", grouped.today, "today")}
                  {renderGroup("Last Week", grouped.recent, "recent")}
                  {renderGroup("Older", grouped.older, "older")}
                </>
              )}
            </div>
          </ScrollArea>
        </aside>

        {/* Center pane - Chat window */}
        <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <>

          {isSectionChat && selectedChat ? (
            selectedChat.sessionId && selectedChat.templateId && convexUser ? (
              <SectionChatLayout
                chatId={selectedChat._id}
                sessionId={selectedChat.sessionId}
                templateId={selectedChat.templateId}
                userId={convexUser._id}
                sectionId={selectedChat.sectionId || ""}
                sectionTitle={selectedChat.sectionTitle || ""}
                initialFocusQuote={pendingFocusQuote}
                onConsumeInitialFocusQuote={() => setPendingFocusQuote(null)}
                contextSlot={renderSectionContextSlot()}
              />
            ) : (
              <div
                className="flex-1 flex items-center justify-center p-6 text-center"
                data-testid="text-section-chat-unavailable"
              >
                <div className="max-w-md text-sm text-muted-foreground">
                  This section chat is missing its linked session or
                  template, so the assistant can't be opened. Try
                  starting a new section assistant from the document.
                </div>
              </div>
            )
          ) : (!selectedChatId || selectedChatId === draftChatId) && !hasMessages && !isSectionChat && !isSentenceChat ? (
            <>
              <ScrollArea className="flex-1 min-h-0">
                <div className="min-h-full flex flex-col items-center justify-center px-4 py-6">
                  <div className="w-full max-w-2xl text-center">
                    <h2 className="text-3xl font-heading font-bold mb-2">
                      How can I help you today?
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Start a new conversation by typing a prompt below.
                    </p>

                    {/* Agent picker */}
                    <div className="flex justify-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            data-testid="button-agent-picker"
                          >
                            <Bot className="w-4 h-4 text-primary" />
                            <span className="text-sm">
                              {pendingAgent ? pendingAgent.name : "Default (no agent)"}
                            </span>
                            <ChevronDown className="w-4 h-4 opacity-60" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center" className="w-64">
                          <DropdownMenuLabel>Choose agent</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setPendingAgentId(null)}
                            data-testid="agent-picker-option-default"
                          >
                            <div className="flex items-center justify-between w-full">
                              <span>Default (no agent)</span>
                              {!pendingAgentId && (
                                <Check className="w-4 h-4 text-primary" />
                              )}
                            </div>
                          </DropdownMenuItem>
                          {agentsList.length > 0 && <DropdownMenuSeparator />}
                          {agentsList.map((a) => (
                            <DropdownMenuItem
                              key={a._id}
                              onClick={() => setPendingAgentId(a._id)}
                              data-testid={`agent-picker-option-${a._id}`}
                            >
                              <div className="flex items-center justify-between w-full gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <Bot className="w-3.5 h-3.5 text-primary shrink-0" />
                                  <div className="flex flex-col min-w-0">
                                    <span className="truncate">{a.name}</span>
                                    {modelLabelFor(a.model, modelOptions) && (
                                      <span className="text-[10px] text-muted-foreground truncate">
                                        {modelLabelFor(a.model, modelOptions)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {pendingAgentId === a._id && (
                                  <Check className="w-4 h-4 text-primary shrink-0" />
                                )}
                              </div>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              </ScrollArea>

              <div className="border-t border-border bg-card p-4 shrink-0">
                <div className="max-w-2xl mx-auto relative">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder="Ask anything..."
                    rows={3}
                    className="w-full p-4 pr-14 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    data-testid="input-new-chat-prompt"
                  />
                  <Button
                    size="icon"
                    className="absolute right-3 bottom-3"
                    onClick={handleSend}
                    disabled={!input.trim() || isStreaming}
                    data-testid="button-send-new-chat"
                  >
                    {isStreaming ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <ScrollArea className="flex-1 min-h-0">
                <div ref={scrollRef} className="max-w-3xl mx-auto px-4 py-6 space-y-6">
                  {(messages || []).map((m) => (
                    <div
                      key={m._id}
                      className={`flex flex-col ${
                        m.role === "user" ? "items-end" : "items-start"
                      }`}
                      data-testid={`message-${m.role}-${m._id}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-4 py-3 ${
                          m.role === "user"
                            ? "bg-primary text-primary-foreground whitespace-pre-wrap"
                            : "bg-muted text-foreground"
                        }`}
                      >
                        {m.role === "user" ? (
                          m.content
                        ) : (
                          <MarkdownMessage content={m.content} />
                        )}
                      </div>
                      <CopyMessageButton messageId={m._id} content={m.content} />
                    </div>
                  ))}
                  {isStreaming && (
                    <div className="flex justify-start" data-testid="message-streaming">
                      <div className="max-w-[80%] rounded-lg px-4 py-3 bg-muted text-foreground">
                        {streamingContent ? (
                          <MarkdownMessage content={streamingContent} />
                        ) : (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>

              <div className="border-t border-border bg-card p-4 shrink-0">
                {isSentenceChat && selectedChat?.focusQuote && (
                  <div
                    className="max-w-3xl mx-auto mb-2 rounded-md border border-yellow-300/70 bg-yellow-50 dark:bg-yellow-950/30 px-3 py-2 text-xs text-foreground"
                    data-testid="banner-sentence-context"
                  >
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                      Discussing sentence
                    </div>
                    <div className="italic" data-testid="text-sentence-focus-quote">
                      "{selectedChat.focusQuote}"
                    </div>
                  </div>
                )}
                <div className="max-w-3xl mx-auto relative">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder={isSentenceChat ? "Try: reword this sentence" : "Send a message..."}
                    rows={2}
                    className="w-full p-3 pr-14 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    data-testid="input-chat-message"
                  />
                  <Button
                    size="icon"
                    className="absolute right-3 bottom-3"
                    onClick={handleSend}
                    disabled={!input.trim() || isStreaming}
                    data-testid="button-send-message"
                  >
                    {isStreaming ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
          </>
        </main>

        {/* Right Pane - Chat-specific controls (model + context) */}
        {!isSectionChat && (
        <aside
          className="w-64 border-l border-border bg-card flex flex-col min-h-0 shrink-0"
          data-testid="pane-chat-controls"
        >
          <div className="p-3 border-b border-border space-y-1">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Context
            </div>
            {isSectionChat && selectedChat?.sectionTitle ? (
              <div
                className="flex flex-col gap-1 text-sm text-foreground min-w-0"
                data-testid="text-section-chat-header"
              >
                {selectedChat.sessionId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 -ml-2 self-start text-muted-foreground hover:text-foreground"
                    onClick={() =>
                      navigate(`/dashboard/session/${selectedChat.sessionId}`)
                    }
                    aria-label="Back to document"
                    data-testid="button-back-to-document"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back to document
                  </Button>
                )}
                <div className="flex items-start gap-1.5 min-w-0">
                  <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Section
                    </div>
                    <div className="text-sm truncate">
                      {selectedChat.sectionTitle}
                    </div>
                  </div>
                </div>
              </div>
            ) : isSentenceChat && selectedChat?.sectionTitle ? (
              <div
                className="flex flex-col gap-1 text-sm text-foreground min-w-0"
                data-testid="text-sentence-chat-header"
              >
                {selectedChat.sessionId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 -ml-2 self-start text-muted-foreground hover:text-foreground"
                    onClick={() =>
                      navigate(`/dashboard/session/${selectedChat.sessionId}`)
                    }
                    aria-label="Back to document"
                    data-testid="button-back-to-document"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back to document
                  </Button>
                )}
                <div className="flex items-start gap-1.5 min-w-0">
                  <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Sentence in
                    </div>
                    <div className="text-sm truncate">
                      {selectedChat.sectionTitle}
                    </div>
                  </div>
                </div>
              </div>
            ) : headerAgentLabel ? (
              <div
                className="flex items-start gap-1.5 text-sm text-foreground min-w-0"
                data-testid="text-chat-agent"
              >
                <Bot className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Agent
                  </div>
                  <div className="text-sm truncate">
                    {selectedChat?.agentDeleted
                      ? "Agent deleted"
                      : selectedChat?.agentName || "No agent"}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">
                No chat selected
              </div>
            )}
          </div>

          {!isSectionChat && !isSentenceChat && (
            <div
              className="p-3 border-b border-border space-y-3"
              data-testid="section-chat-attachments"
            >
              {renderAttachmentsSection()}
            </div>
          )}

          <div className="p-3 border-b border-border space-y-1.5">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Model
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-between gap-1.5 h-8"
                  data-testid="button-model-selector"
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Sparkles className="w-3.5 h-3.5 text-primary shrink-0" />
                    <span className="text-xs font-medium truncate">
                      {selectedModel?.label ?? effectiveModelId}
                    </span>
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 opacity-60 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {modelOptions.map((opt) => {
                  const isSelected = opt.id === effectiveModelId;
                  return (
                    <DropdownMenuItem
                      key={opt.id}
                      onClick={() => handleSelectModel(opt.id)}
                      data-testid={`menu-model-${opt.id}`}
                    >
                      <div className="flex items-center justify-between w-full gap-2">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{opt.label}</span>
                          {opt.provider && (
                            <span className="text-xs text-muted-foreground">{opt.provider}</span>
                          )}
                        </div>
                        {isSelected && <Check className="w-4 h-4 text-primary" />}
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            <div
              className="text-[10px] text-muted-foreground truncate"
              data-testid="text-model-label"
            >
              {selectedModel?.provider ?? ""}
            </div>
          </div>
        </aside>
        )}
      </div>

      {/* Agent create/edit dialog */}
      <Dialog open={agentDialogOpen} onOpenChange={setAgentDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingAgent ? "Edit agent" : "New agent"}
            </DialogTitle>
            <DialogDescription>
              Agents let you start chats with a custom system prompt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="agent-name">Name</Label>
              <Input
                id="agent-name"
                value={agentFormName}
                onChange={(e) => setAgentFormName(e.target.value)}
                placeholder="e.g. Psychiatry Professional"
                data-testid="input-agent-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="agent-description">Description (optional)</Label>
              <Input
                id="agent-description"
                value={agentFormDescription}
                onChange={(e) => setAgentFormDescription(e.target.value)}
                placeholder="Short note about what this agent is for"
                data-testid="input-agent-description"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="agent-model">Model</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    id="agent-model"
                    variant="outline"
                    className="w-full justify-between"
                    data-testid="button-agent-model"
                  >
                    <span className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      <span className="text-sm">
                        {modelLabelFor(agentFormModel, modelOptions) ||
                          modelLabelFor(effectiveDefaultModelId, modelOptions)}
                      </span>
                    </span>
                    <ChevronDown className="w-4 h-4 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {modelOptions.map((opt) => (
                    <DropdownMenuItem
                      key={opt.id}
                      onClick={() => setAgentFormModel(opt.id)}
                      data-testid={`menu-agent-model-${opt.id}`}
                    >
                      <div className="flex items-center justify-between w-full gap-2">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {opt.label}
                          </span>
                          {opt.provider && (
                            <span className="text-xs text-muted-foreground">
                              {opt.provider}
                            </span>
                          )}
                        </div>
                        {opt.id === agentFormModel && (
                          <Check className="w-4 h-4 text-primary" />
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="space-y-2">
              <Label htmlFor="agent-prompt">System prompt</Label>
              <textarea
                id="agent-prompt"
                value={agentFormPrompt}
                onChange={(e) => setAgentFormPrompt(e.target.value)}
                placeholder="You are a..."
                rows={10}
                className="w-full p-3 rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-y min-h-[200px] text-sm"
                data-testid="input-agent-prompt"
              />
            </div>
          </div>
          <DialogFooter className="flex sm:justify-between gap-2">
            <div>
              {editingAgent && (
                <Button
                  variant="outline"
                  onClick={() => openHistoryForAgent(editingAgent._id)}
                  data-testid="button-agent-history"
                >
                  <History className="w-4 h-4 mr-2" />
                  History
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setAgentDialogOpen(false)}
                data-testid="button-agent-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveAgent}
                disabled={agentSaving}
                data-testid="button-agent-save"
              >
                {agentSaving && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                {editingAgent ? "Save changes" : "Create agent"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Agent history side panel */}
      <Sheet
        open={historyAgentId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setHistoryAgentId(null);
            setSelectedVersionId(null);
          }
        }}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-4xl flex flex-col gap-0 p-0"
        >
          <SheetHeader className="p-4 border-b border-border">
            <SheetTitle>Version history</SheetTitle>
            <SheetDescription>
              Earlier saves of this agent's name, description, model, and system
              prompt. Restore to roll back.
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-1 min-h-0">
            {/* Version list */}
            <div className="w-72 border-r border-border flex flex-col shrink-0">
              <ScrollArea className="flex-1">
                {agentVersions === undefined ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : agentVersions.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-8 px-4">
                    No history yet.
                  </div>
                ) : (
                  <div className="py-2">
                    {agentVersions.map((v: Doc<"agentVersions">, idx: number) => {
                      const isCurrent = idx === 0;
                      const isSelected = selectedVersionId === v._id;
                      const preview = v.systemPrompt
                        .replace(/\s+/g, " ")
                        .slice(0, 80);
                      return (
                        <button
                          key={v._id}
                          onClick={() => setSelectedVersionId(v._id)}
                          className={`w-full text-left px-3 py-2 mx-2 my-1 rounded-md text-sm transition-colors ${
                            isSelected
                              ? "bg-primary/10 text-primary"
                              : "hover:bg-muted"
                          }`}
                          data-testid={`agent-version-${v._id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium truncate">
                              {new Date(v.createdAt).toLocaleString()}
                            </span>
                            {isCurrent && (
                              <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground truncate mt-0.5">
                            {preview || "(empty)"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Diff view */}
            <div className="flex-1 flex flex-col min-w-0">
              {(() => {
                const versions: Doc<"agentVersions">[] = agentVersions || [];
                const current = versions[0];
                const selected = versions.find(
                  (v) => v._id === selectedVersionId
                );
                if (!selected) {
                  return (
                    <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground p-6">
                      Select a version on the left to see what changed.
                    </div>
                  );
                }
                const isCurrent = selected._id === current?._id;
                const diff = diffLines(
                  selected.systemPrompt || "",
                  current?.systemPrompt || ""
                );
                const fieldRow = (
                  label: string,
                  before: string | undefined,
                  after: string | undefined
                ) => {
                  const changed = (before || "") !== (after || "");
                  return (
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <div className="text-muted-foreground mb-1">
                          {label} (this version)
                        </div>
                        <div
                          className={`rounded px-2 py-1 border ${
                            changed
                              ? "bg-destructive/10 border-destructive/30"
                              : "bg-muted/40 border-border"
                          }`}
                          data-testid={`diff-${label.toLowerCase()}-before`}
                        >
                          {before || (
                            <span className="text-muted-foreground italic">
                              (empty)
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground mb-1">
                          {label} (current)
                        </div>
                        <div
                          className={`rounded px-2 py-1 border ${
                            changed
                              ? "bg-green-500/10 border-green-500/30"
                              : "bg-muted/40 border-border"
                          }`}
                          data-testid={`diff-${label.toLowerCase()}-after`}
                        >
                          {after || (
                            <span className="text-muted-foreground italic">
                              (empty)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                };
                return (
                  <>
                    <div className="border-b border-border p-4 space-y-3">
                      {fieldRow("Name", selected.name, current?.name)}
                      {fieldRow(
                        "Description",
                        selected.description,
                        current?.description
                      )}
                      {fieldRow(
                        "Model",
                        modelLabelFor(selected.model, modelOptions) || "Default",
                        modelLabelFor(current?.model, modelOptions) || "Default"
                      )}
                    </div>
                    <div className="flex-1 min-h-0 flex flex-col">
                      <div className="grid grid-cols-2 gap-0 px-4 pt-3 text-xs text-muted-foreground">
                        <div>System prompt (this version)</div>
                        <div>System prompt (current)</div>
                      </div>
                      <ScrollArea className="flex-1 px-4 pb-4 pt-2">
                        <div
                          className="grid grid-cols-2 gap-0 font-mono text-xs border border-border rounded overflow-hidden"
                          data-testid="diff-systemprompt"
                        >
                          {diff.left.map((l, idx) => {
                            const r = diff.right[idx];
                            return (
                              <div key={idx} className="contents">
                                <div
                                  className={`px-2 py-0.5 whitespace-pre-wrap border-r border-border ${
                                    l?.type === "del"
                                      ? "bg-destructive/10 text-destructive-foreground"
                                      : l
                                        ? ""
                                        : "bg-muted/30"
                                  }`}
                                >
                                  {l ? l.text || "\u00A0" : "\u00A0"}
                                </div>
                                <div
                                  className={`px-2 py-0.5 whitespace-pre-wrap ${
                                    r?.type === "add"
                                      ? "bg-green-500/10"
                                      : r
                                        ? ""
                                        : "bg-muted/30"
                                  }`}
                                >
                                  {r ? r.text || "\u00A0" : "\u00A0"}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    </div>
                    <div className="border-t border-border p-3 flex justify-end gap-2">
                      <Button
                        onClick={handleRestoreVersion}
                        disabled={isCurrent || restoringVersion}
                        data-testid="button-restore-version"
                      >
                        {restoringVersion ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RotateCcw className="w-4 h-4 mr-2" />
                        )}
                        Restore this version
                      </Button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Starter agents library dialog */}
      <Dialog open={starterDialogOpen} onOpenChange={setStarterDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Library className="w-5 h-5 text-primary" />
              Starter reviewer agents
            </DialogTitle>
            <DialogDescription>
              Clone any of these curated agents to your account. You can edit
              the clone freely — the original stays read-only.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-3 pb-2">
              {STARTER_AGENTS.map((s) => {
                const expanded = expandedStarterId === s.id;
                const isCloning = cloningStarterId === s.id;
                return (
                  <div
                    key={s.id}
                    className="border border-border rounded-md p-3"
                    data-testid={`starter-card-${s.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-1.5 bg-primary/10 rounded-md shrink-0">
                        <Sparkles className="w-4 h-4 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm" data-testid={`starter-name-${s.id}`}>
                          {s.name}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {s.description}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleCloneStarterAgent(s)}
                        disabled={isCloning}
                        data-testid={`button-use-starter-${s.id}`}
                      >
                        {isCloning ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        ) : (
                          <Plus className="w-3.5 h-3.5 mr-1" />
                        )}
                        Use this agent
                      </Button>
                    </div>
                    <button
                      onClick={() =>
                        setExpandedStarterId(expanded ? null : s.id)
                      }
                      className="mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                      data-testid={`button-toggle-starter-prompt-${s.id}`}
                    >
                      {expanded ? (
                        <ChevronDown className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5" />
                      )}
                      {expanded ? "Hide system prompt" : "Preview system prompt"}
                    </button>
                    {expanded && (
                      <pre
                        className="mt-2 p-3 rounded-md bg-muted text-xs whitespace-pre-wrap font-mono max-h-64 overflow-auto"
                        data-testid={`starter-prompt-${s.id}`}
                      >
                        {s.systemPrompt}
                      </pre>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setStarterDialogOpen(false)}
              data-testid="button-starter-dialog-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={deleteAgentId !== null}
        onOpenChange={(open) => !open && setDeleteAgentId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete agent?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the agent. Existing chats that use it will keep
              working but no longer apply its system prompt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-confirm-delete-agent-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteAgent}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-agent"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </TooltipProvider>
  );
}

function SectionChatLayout({
  chatId,
  sessionId,
  templateId,
  userId,
  sectionId,
  sectionTitle,
  initialFocusQuote,
  onConsumeInitialFocusQuote,
  contextSlot,
}: {
  chatId: Id<"chats">;
  sessionId: Id<"recordingSessions">;
  templateId: Id<"templates">;
  userId: Id<"users">;
  sectionId: string;
  sectionTitle: string;
  initialFocusQuote: string | null;
  onConsumeInitialFocusQuote: () => void;
  contextSlot?: React.ReactNode;
}) {
  const session = useQuery(api.recordingSessions.get, { id: sessionId, userId });
  const templateSections = useQuery(api.templates.getSections, { templateId, userId });
  const template = useQuery(api.templates.get, { id: templateId, userId });
  const globalRules = useQuery(api.globalRules.list, { userId });
  const ruleTags = useQuery(api.ruleTags.list, { userId });
  const applyEdit = useMutation(api.recordingSessions.applySectionContentEdit);

  const [openRefs, setOpenRefs] = useState<Record<string, boolean>>({});
  const [refsDrawerOpen, setRefsDrawerOpen] = useState(false);
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const toggleRef = (k: string) =>
    setOpenRefs((p) => ({ ...p, [k]: !p[k] }));

  const sectionContent = useMemo(() => {
    if (!session?.generatedDocument) return "";
    const doc = session.generatedDocument;
    const escaped = sectionTitle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`^##\\s+${escaped}\\s*\\n([\\s\\S]*?)(?=^##\\s+|\\Z)`, "m");
    const match = doc.match(re);
    return match?.[1]?.trim() || "";
  }, [session?.generatedDocument, sectionTitle]);

  const sections = (templateSections ?? []) as Doc<"templateSections">[];
  const rules = (globalRules ?? []) as Doc<"globalRules">[];
  const tags = (ruleTags ?? []) as Doc<"ruleTags">[];

  const templateSection = useMemo<Doc<"templateSections"> | undefined>(() => {
    return sections.find(
      (s) => String(s._id) === sectionId || s.name === sectionTitle,
    );
  }, [sections, sectionId, sectionTitle]);

  type RuleEntry = { _id: string; content: string };
  type TagGroup = { tagId: string; tagName: string; rules: RuleEntry[] };

  const rulesByTag = useMemo<TagGroup[]>(() => {
    if (!templateSection?.tagIds) return [];
    const tagIds = templateSection.tagIds.map((id) => String(id));
    const tagsById = new Map(tags.map((t) => [String(t._id), t]));
    const rulesByTagId = new Map<string, RuleEntry[]>();
    for (const r of rules) {
      if (!r.tagId) continue;
      const tid = String(r.tagId);
      if (!tagIds.includes(tid)) continue;
      const arr = rulesByTagId.get(tid) ?? [];
      arr.push({ _id: String(r._id), content: r.content });
      rulesByTagId.set(tid, arr);
    }
    return tagIds.map((tid) => ({
      tagId: tid,
      tagName: tagsById.get(tid)?.name ?? "tag",
      rules: rulesByTagId.get(tid) ?? [],
    }));
  }, [templateSection, rules, tags]);

  const taggedRuleCount = rulesByTag.reduce((n, t) => n + t.rules.length, 0);
  const examples: string[] = templateSection?.examples ?? [];
  const formatTemplate: string = templateSection?.formatTemplate ?? "";
  const description: string = templateSection?.description ?? "";

  if (!session || !templateSections || !template) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const transcription = session.transcription ?? "";
  const notes = (session as Doc<"recordingSessions">).notes ?? "";

  type RefRow = {
    key: string;
    label: string;
    hint: string;
    render: () => React.ReactNode;
    previewText: string;
  };
  const truncate = (s: string, n = 80) =>
    s.length > n ? s.slice(0, n).replace(/\s+\S*$/, "") + "…" : s;
  const charHint = (n: number) => `${n.toLocaleString()} char${n === 1 ? "" : "s"}`;
  const refRows: RefRow[] = [];
  if (sectionContent) {
    refRows.push({
      key: "section",
      label: "Generated section content",
      hint: charHint(sectionContent.length),
      previewText: truncate(sectionContent),
      render: () => <div className="whitespace-pre-wrap">{sectionContent}</div>,
    });
  }
  if (description) {
    refRows.push({
      key: "description",
      label: "Template section description",
      hint: charHint(description.length),
      previewText: truncate(description),
      render: () => <div className="whitespace-pre-wrap">{description}</div>,
    });
  }
  if (rulesByTag.some((t) => t.rules.length > 0)) {
    refRows.push({
      key: "rules",
      label: "Tagged contents",
      hint: `${rulesByTag.length} tag${rulesByTag.length === 1 ? "" : "s"} · ${taggedRuleCount} rule${taggedRuleCount === 1 ? "" : "s"}`,
      previewText: rulesByTag
        .filter((t) => t.rules.length > 0)
        .map((t) => t.tagName)
        .join(", "),
      render: () => (
        <div className="space-y-3">
          {rulesByTag
            .filter((t) => t.rules.length > 0)
            .map((tag) => (
              <div key={tag.tagId} data-testid={`reference-tag-${tag.tagId}`}>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-foreground/70 mb-1">
                  {tag.tagName} ({tag.rules.length})
                </div>
                <ul className="space-y-1 list-disc list-inside">
                  {tag.rules.map((r) => (
                    <li
                      key={r._id}
                      className="whitespace-pre-wrap"
                      data-testid={`reference-rule-${r._id}`}
                    >
                      {r.content}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </div>
      ),
    });
  }
  if (examples.length > 0) {
    refRows.push({
      key: "examples",
      label: "Examples",
      hint: `${examples.length} example${examples.length === 1 ? "" : "s"}`,
      previewText: truncate(examples[0] ?? ""),
      render: () => (
        <div className="space-y-3">
          {examples.map((ex, i) => (
            <div key={i} data-testid={`reference-example-${i}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-foreground/70 mb-1">
                Example {i + 1}
              </div>
              <div className="whitespace-pre-wrap">{ex}</div>
            </div>
          ))}
        </div>
      ),
    });
  }
  if (formatTemplate) {
    refRows.push({
      key: "format",
      label: "Format template",
      hint: charHint(formatTemplate.length),
      previewText: truncate(formatTemplate),
      render: () => <div className="whitespace-pre-wrap">{formatTemplate}</div>,
    });
  }
  if (transcription) {
    refRows.push({
      key: "transcript",
      label: "Transcription",
      hint: charHint(transcription.length),
      previewText: truncate(transcription),
      render: () => <div className="whitespace-pre-wrap">{transcription}</div>,
    });
  }
  if (notes) {
    refRows.push({
      key: "notes",
      label: "Notes",
      hint: charHint(notes.length),
      previewText: truncate(notes),
      render: () => <div className="whitespace-pre-wrap">{notes}</div>,
    });
  }

  const renderRows = () => (
    <div className="p-2 space-y-1">
      {refRows.length === 0 ? (
        <div
          className="px-3 py-4 text-xs text-muted-foreground"
          data-testid="text-references-empty"
        >
          No references available for this section.
        </div>
      ) : (
        refRows.map((row) => {
          const isOpen = !!openRefs[row.key];
          return (
            <div
              key={row.key}
              className="border border-border rounded-md"
              data-testid={`reference-row-${row.key}`}
            >
              <button
                type="button"
                onClick={() => toggleRef(row.key)}
                className="w-full flex items-start justify-between gap-2 px-3 py-2 text-left hover:bg-muted"
                data-testid={`button-toggle-reference-${row.key}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-xs font-medium truncate">{row.label}</div>
                    <div
                      className="text-[10px] text-muted-foreground shrink-0"
                      data-testid={`reference-hint-${row.key}`}
                    >
                      {row.hint}
                    </div>
                  </div>
                  {!isOpen && row.previewText && (
                    <div
                      className="text-[11px] text-muted-foreground truncate mt-0.5"
                      data-testid={`reference-preview-${row.key}`}
                    >
                      {row.previewText}
                    </div>
                  )}
                </div>
                {isOpen ? (
                  <ChevronDown className="w-3.5 h-3.5 opacity-60 mt-0.5 shrink-0" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 opacity-60 mt-0.5 shrink-0" />
                )}
              </button>
              {isOpen && (
                <div className="px-3 pb-3 pt-1 text-xs text-muted-foreground border-t border-border max-h-64 overflow-y-auto">
                  {row.render()}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <div className="flex-1 flex overflow-hidden">
      <div className="flex-1 min-w-0 flex flex-col relative">
        <div className="lg:hidden absolute right-3 top-3 z-10">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRefsDrawerOpen(true)}
            data-testid="button-open-references-drawer"
            className="gap-1.5 h-8"
          >
            <Library className="w-3.5 h-3.5" />
            References
            {refRows.length > 0 && (
              <span className="text-[10px] text-muted-foreground">
                ({refRows.length})
              </span>
            )}
          </Button>
        </div>
        <div className="px-4 pt-3">
          <LearnFromEditsIndicator
            sessionId={sessionId}
            sectionTitle={sectionTitle}
            userId={userId}
          />
        </div>
        <SectionAssistant
          chatId={chatId}
          sessionId={sessionId}
          templateId={templateId}
          userId={userId}
          sectionTitle={sectionTitle}
          sectionContent={sectionContent}
          transcription={session.transcription || ""}
          templateSections={sections}
          onApplySectionContent={async (newValue, opts) => {
            await applyEdit({
              id: sessionId,
              userId,
              sectionTitle,
              newContent: newValue,
              provenance: opts?.provenance,
              summaryOverride: opts?.summaryOverride,
            });
          }}
          initialFocusQuote={initialFocusQuote}
          onConsumeInitialFocusQuote={onConsumeInitialFocusQuote}
          selectedAgentIds={selectedAgentIds}
          setSelectedAgentIds={setSelectedAgentIds}
          hideHeader
        />
      </div>
      <aside
        className="w-80 border-l border-border bg-card hidden lg:flex flex-col shrink-0 overflow-hidden"
        data-testid="pane-references"
      >
        {contextSlot}
        <SectionAssistantHeader
          userId={userId}
          sectionTitle={sectionTitle}
          selectedAgentIds={selectedAgentIds}
          setSelectedAgentIds={setSelectedAgentIds}
        />
        <div className="p-4 border-b border-border shrink-0">
          <div className="text-sm font-semibold">References</div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {sectionTitle}
          </div>
        </div>
        <ScrollArea className="flex-1">{renderRows()}</ScrollArea>
      </aside>
      <Sheet open={refsDrawerOpen} onOpenChange={setRefsDrawerOpen}>
        <SheetContent
          side="right"
          className="w-[85vw] sm:w-96 p-0 flex flex-col"
          data-testid="drawer-references"
        >
          <SectionAssistantHeader
            userId={userId}
            sectionTitle={sectionTitle}
            selectedAgentIds={selectedAgentIds}
            setSelectedAgentIds={setSelectedAgentIds}
          />
          <SheetHeader className="p-4 border-b border-border">
            <SheetTitle>References</SheetTitle>
            <SheetDescription className="truncate">
              {sectionTitle}
            </SheetDescription>
          </SheetHeader>
          <ScrollArea className="flex-1">{renderRows()}</ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  );
}
